import React, { useEffect, useState } from 'react';
import BackButton from "../components/BackButton";
import HatchEntriesService from '../services/HatchEntries';

const ORDER_COLORS = {
  mayfly: { bg: '#198754', text: '#fff' },
  caddis: { bg: '#fd7e14', text: '#fff' },
  stonefly: { bg: '#dc3545', text: '#fff' },
  midge: { bg: '#0d6efd', text: '#fff' },
  terrestrial: { bg: '#795548', text: '#fff' },
  other: { bg: '#6c757d', text: '#fff' },
};

const STATUS_COLORS = {
  active: { bg: '#198754', text: '#fff' },
  upcoming: { bg: '#ffc107', text: '#000' },
  'off-season': { bg: '#6c757d', text: '#fff' },
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const FULL_MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function OrderBadge({ order }) {
  const c = ORDER_COLORS[order] || ORDER_COLORS.other;
  return (
    <span style={{
      backgroundColor: c.bg, color: c.text,
      padding: '2px 8px', borderRadius: '10px',
      fontSize: '0.75em', fontWeight: 'bold',
      textTransform: 'capitalize',
    }}>
      {order}
    </span>
  );
}

function StatusBadge({ status }) {
  const c = STATUS_COLORS[status] || STATUS_COLORS['off-season'];
  const label = status === 'off-season' ? 'Off Season' : status.charAt(0).toUpperCase() + status.slice(1);
  return (
    <span style={{
      backgroundColor: c.bg, color: c.text,
      padding: '2px 8px', borderRadius: '10px',
      fontSize: '0.75em', fontWeight: 'bold',
    }}>
      {label}
    </span>
  );
}

function formatHatchWindow(e) {
  if (!e.hatch_start_month || !e.hatch_end_month) return '—';
  if (e.hatch_start_month === 1 && e.hatch_start_day <= 1 && e.hatch_end_month === 12 && e.hatch_end_day >= 28) {
    return 'Year-round';
  }
  const start = `${MONTHS[e.hatch_start_month - 1]} ${e.hatch_start_day}`;
  const end = `${MONTHS[e.hatch_end_month - 1]} ${e.hatch_end_day}`;
  return `${start} — ${end}`;
}

function formatHookSize(e) {
  if (!e.hook_size_min && !e.hook_size_max) return '—';
  if (e.hook_size_min === e.hook_size_max) return `#${e.hook_size_min}`;
  return `#${e.hook_size_min}-${e.hook_size_max}`;
}

function ExpandableText({ text, maxLength = 200 }) {
  const [expanded, setExpanded] = useState(false);
  if (!text) return <span style={{ color: '#999' }}>—</span>;
  if (text.length <= maxLength) return <span>{text}</span>;
  return (
    <div>
      {expanded ? text : text.substring(0, maxLength) + '...'}
      <button
        onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
        style={{
          background: 'none', border: 'none', color: '#0d6efd',
          fontSize: '0.85em', cursor: 'pointer', padding: '0 4px',
          textDecoration: 'underline',
        }}>
        {expanded ? 'Less' : 'More'}
      </button>
    </div>
  );
}

function ExpandableList({ items, maxShow = 3 }) {
  const [expanded, setExpanded] = useState(false);
  if (!items || items.length === 0) return <span style={{ color: '#999' }}>—</span>;
  const showing = expanded ? items : items.slice(0, maxShow);
  return (
    <div style={{ fontSize: '0.8em' }}>
      {showing.map((item, i) => (
        <div key={i} style={{ marginBottom: '1px' }}>{item}</div>
      ))}
      {items.length > maxShow && (
        <button
          onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
          style={{
            background: 'none', border: 'none', color: '#0d6efd',
            fontSize: '0.9em', cursor: 'pointer', padding: 0, textDecoration: 'underline',
          }}>
          {expanded ? 'Less' : `+${items.length - maxShow} more`}
        </button>
      )}
    </div>
  );
}

function SummaryCard({ label, value, color }) {
  return (
    <div style={{
      backgroundColor: color || '#f8f9fa',
      borderRadius: '8px', padding: '12px 16px',
      textAlign: 'center', minWidth: '120px',
    }}>
      <div style={{ fontSize: '1.8em', fontWeight: 'bold' }}>{value}</div>
      <div style={{ fontSize: '0.8em', color: '#666' }}>{label}</div>
    </div>
  );
}

const CELL_STYLE = {
  wordWrap: 'break-word', overflowWrap: 'break-word',
  whiteSpace: 'normal', verticalAlign: 'middle', fontSize: '0.85em',
};

const HatchTracker = () => {
  const [entries, setEntries] = useState([]);
  const [rivers, setRivers] = useState([]);
  const [regions, setRegions] = useState([]);
  const [activeCount, setActiveCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [riversCount, setRiversCount] = useState(0);
  const [filters, setFilters] = useState({
    river: '', order: '', region: '', month: '', active: false, search: '',
    date: new Date().toISOString().slice(0, 10),
  });
  const [sortField, setSortField] = useState('river_name');
  const [sortDir, setSortDir] = useState('asc');
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshingConditions, setRefreshingConditions] = useState(false);

  function loadEntries() {
    setLoading(true);
    HatchEntriesService.getHatchEntries(filters).then((resp) => {
      setEntries(resp.entries || []);
      setRivers(resp.rivers || []);
      setRegions(resp.regions || []);
      setActiveCount(resp.active_count || 0);
      setTotalCount(resp.total_count || 0);
      setRiversCount(resp.rivers_count || 0);
      setLoading(false);
    }).catch(() => setLoading(false));
  }

  useEffect(() => {
    loadEntries();
  }, [filters.river, filters.order, filters.region, filters.month, filters.active, filters.date]);

  function handleSearch() { loadEntries(); }

  function handleSort(field) {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  }

  function sortedEntries() {
    return [...entries].sort((a, b) => {
      let aVal = a[sortField] || '';
      let bVal = b[sortField] || '';
      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();
      // Sort by hatch_start_month numerically
      if (sortField === 'hatch_start_month') {
        aVal = (a.hatch_start_month || 0) * 100 + (a.hatch_start_day || 0);
        bVal = (b.hatch_start_month || 0) * 100 + (b.hatch_start_day || 0);
      }
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }

  function sortArrow(field) {
    if (sortField !== field) return '';
    return sortDir === 'asc' ? ' ▲' : ' ▼';
  }

  function handleDeleteEntry(id, name) {
    if (!window.confirm(`Delete "${name}"?`)) return;
    HatchEntriesService.deleteEntry(id).then(() => loadEntries());
  }

  function handleRefreshConditions() {
    setRefreshingConditions(true);
    HatchEntriesService.refreshConditions().then(() => {
      setTimeout(() => {
        setRefreshingConditions(false);
        loadEntries();
      }, 20000);
    });
  }

  const currentMonth = new Date().getMonth() + 1;

  return (
    <div className="m-5">
      <div className="container-fluid">
        <BackButton />
        <h2 className="text-center">Colorado Hatch Tracker</h2>
        <p className="text-center text-muted" style={{ fontSize: '0.85em' }}>
          Insect hatch data for Colorado rivers — {totalCount} entries across {riversCount} rivers
        </p>

        {/* Summary Cards */}
        <div className="d-flex justify-content-center gap-3 mb-4">
          <SummaryCard label="Total Entries" value={totalCount} />
          <SummaryCard label="Active Now" value={activeCount} color="#d4edda" />
          <SummaryCard label="Rivers" value={riversCount} />
          <SummaryCard label="Current Month" value={FULL_MONTHS[currentMonth - 1]} />
        </div>

        {/* Date Picker */}
        <div className="row mb-3 g-2 align-items-end">
          <div className="col-md-3">
            <label className="form-label mb-1" style={{ fontWeight: 'bold', fontSize: '0.9em' }}>
              What's hatching on this date?
            </label>
            <input
              type="date"
              className="form-control form-control-sm"
              value={filters.date}
              onChange={(e) => setFilters({ ...filters, date: e.target.value })}
            />
          </div>
          <div className="col-md-1 pt-4">
            <button
              className="btn btn-sm btn-outline-primary w-100"
              onClick={() => setFilters({ ...filters, date: new Date().toISOString().slice(0, 10) })}>
              Today
            </button>
          </div>
          <div className="col-md-2 pt-4">
            <button
              className="btn btn-sm btn-success w-100"
              onClick={handleRefreshConditions}
              disabled={refreshingConditions}>
              {refreshingConditions ? 'Scraping (~20s)...' : 'Refresh Reports'}
            </button>
          </div>
          <div className="col-md-7 pt-4">
            <span className="text-muted" style={{ fontSize: '0.85em' }}>
              {loading ? 'Loading...' : `${activeCount} active hatches across ${riversCount} rivers for this date`}
            </span>
          </div>
        </div>

        {/* Filters */}
        <div className="row mb-3 g-2">
          <div className="col-md-2">
            <select className="form-select form-select-sm" value={filters.river}
              onChange={(e) => setFilters({ ...filters, river: e.target.value })}>
              <option value="">All Rivers</option>
              {rivers.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div className="col-md-2">
            <select className="form-select form-select-sm" value={filters.order}
              onChange={(e) => setFilters({ ...filters, order: e.target.value })}>
              <option value="">All Orders</option>
              <option value="mayfly">Mayfly</option>
              <option value="caddis">Caddis</option>
              <option value="stonefly">Stonefly</option>
              <option value="midge">Midge</option>
              <option value="terrestrial">Terrestrial</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div className="col-md-2">
            <select className="form-select form-select-sm" value={filters.region}
              onChange={(e) => setFilters({ ...filters, region: e.target.value })}>
              <option value="">All Regions</option>
              {regions.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div className="col-md-1">
            <button
              className={`btn btn-sm w-100 ${filters.active ? 'btn-success' : 'btn-outline-success'}`}
              onClick={() => setFilters({ ...filters, active: !filters.active })}
              title="Show only hatches active on selected date">
              Active
            </button>
          </div>
          <div className="col-md-4">
            <div className="input-group input-group-sm">
              <input type="text" className="form-control" placeholder="Search hatches, flies..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
              <button className="btn btn-outline-secondary" onClick={handleSearch}>Search</button>
            </div>
          </div>
          <div className="col-md-1">
            <span className="text-muted" style={{ fontSize: '0.85em', lineHeight: '31px' }}>
              {loading ? '...' : `${entries.length}`}
            </span>
          </div>
        </div>

        {/* Table */}
        <div style={{ overflowX: 'auto' }}>
          <table className="table table-hover table-bordered table-striped table-sm" style={{ tableLayout: 'fixed', width: '100%' }}>
            <colgroup>
              <col style={{ width: '180px' }} />  {/* River / Section */}
              <col style={{ width: '80px' }} />   {/* Status */}
              <col style={{ width: '160px' }} />  {/* Insect */}
              <col style={{ width: '60px' }} />   {/* Hook */}
              <col style={{ width: '120px' }} />  {/* Window */}
              <col style={{ width: '80px' }} />   {/* Peak */}
              <col style={{ width: '200px' }} />  {/* Recommended Flies */}
              <col style={{ width: '220px' }} />  {/* Notes & Tips */}
              <col style={{ width: '300px' }} />  {/* Current Conditions */}
            </colgroup>
            <thead className="table-secondary">
              <tr>
                <th style={{ ...CELL_STYLE, cursor: 'pointer' }} onClick={() => handleSort('river_name')}>
                  River / Section{sortArrow('river_name')}
                </th>
                <th style={{ ...CELL_STYLE, cursor: 'pointer' }} onClick={() => handleSort('hatch_status')}>
                  Status{sortArrow('hatch_status')}
                </th>
                <th style={{ ...CELL_STYLE, cursor: 'pointer' }} onClick={() => handleSort('insect_name')}>
                  Insect{sortArrow('insect_name')}
                </th>
                <th style={CELL_STYLE}>Hook</th>
                <th style={{ ...CELL_STYLE, cursor: 'pointer' }} onClick={() => handleSort('hatch_start_month')}>
                  Window{sortArrow('hatch_start_month')}
                </th>
                <th style={CELL_STYLE}>Peak</th>
                <th style={CELL_STYLE}>Recommended Flies</th>
                <th style={CELL_STYLE}>Notes & Tips</th>
                <th style={CELL_STYLE}>Current Conditions</th>
              </tr>
            </thead>
            <tbody>
              {sortedEntries().map((e) => (
                <React.Fragment key={e.id}>
                  <tr
                    onClick={() => setSelectedEntry(selectedEntry?.id === e.id ? null : e)}
                    style={{
                      cursor: 'pointer',
                      backgroundColor: e.is_active ? '#d4edda' : undefined,
                    }}
                    className={selectedEntry?.id === e.id ? 'table-active' : ''}>
                    {/* River / Section */}
                    <td style={CELL_STYLE}>
                      <div style={{ fontWeight: 'bold' }}>{e.river_name}</div>
                      <div style={{ fontSize: '0.75em', color: '#666' }}>{e.river_region}</div>
                    </td>
                    {/* Status */}
                    <td style={{ ...CELL_STYLE, textAlign: 'center' }}>
                      <StatusBadge status={e.hatch_status} />
                    </td>
                    {/* Insect + Order */}
                    <td style={CELL_STYLE}>
                      <div>{e.insect_name}</div>
                      <div style={{ marginTop: '2px' }}><OrderBadge order={e.insect_order} /></div>
                    </td>
                    {/* Hook Size */}
                    <td style={{ ...CELL_STYLE, textAlign: 'center', fontWeight: 'bold' }}>
                      {formatHookSize(e)}
                    </td>
                    {/* Hatch Window */}
                    <td style={{ ...CELL_STYLE, fontSize: '0.8em' }}>{formatHatchWindow(e)}</td>
                    {/* Peak */}
                    <td style={{ ...CELL_STYLE, fontSize: '0.8em', fontWeight: e.is_active ? 'bold' : 'normal' }}>
                      {e.peak_months || '—'}
                    </td>
                    {/* Recommended Flies — show all */}
                    <td style={CELL_STYLE}>
                      {e.recommended_flies && e.recommended_flies.length > 0 ? (
                        <div style={{ fontSize: '0.8em' }}>
                          {e.recommended_flies.map((fly, i) => (
                            <span key={i} style={{
                              display: 'inline-block',
                              backgroundColor: '#e9ecef',
                              padding: '1px 5px',
                              borderRadius: '3px',
                              margin: '1px 2px',
                              fontSize: '0.9em',
                            }}>{fly}</span>
                          ))}
                        </div>
                      ) : <span style={{ color: '#999' }}>—</span>}
                    </td>
                    {/* Notes & Tips — full text, wrapped */}
                    <td style={{ ...CELL_STYLE, fontSize: '0.8em', lineHeight: '1.4' }}>
                      {e.notes || '—'}
                      {e.life_stages && e.life_stages.length > 0 && (
                        <div style={{ marginTop: '4px' }}>
                          <strong>Stages:</strong>{' '}
                          {e.life_stages.map((s, i) => (
                            <span key={i} style={{
                              backgroundColor: '#d4edda', padding: '0px 4px',
                              borderRadius: '3px', fontSize: '0.9em', marginRight: '3px',
                            }}>{s}</span>
                          ))}
                        </div>
                      )}
                    </td>
                    {/* Current Conditions */}
                    <td style={{ ...CELL_STYLE, fontSize: '0.8em', lineHeight: '1.4', wordWrap: 'break-word', overflowWrap: 'break-word', whiteSpace: 'normal', maxWidth: '300px' }}>
                      {e.current_conditions ? (
                        <div>
                          <ExpandableText text={e.current_conditions} maxLength={200} />
                          {e.conditions_sources && e.conditions_sources.length > 0 && (
                            <div style={{ marginTop: '4px' }}>
                              {e.conditions_sources.map((src, i) => (
                                <a key={i} href={src.url} target="_blank" rel="noopener noreferrer"
                                  onClick={(ev) => ev.stopPropagation()}
                                  style={{
                                    display: 'inline-block', backgroundColor: '#e9ecef',
                                    padding: '1px 5px', borderRadius: '3px', fontSize: '0.8em',
                                    marginRight: '3px', marginBottom: '2px', color: '#0d6efd',
                                    textDecoration: 'none',
                                  }}>
                                  {src.name}
                                </a>
                              ))}
                            </div>
                          )}
                          {e.conditions_updated_at && (
                            <div style={{ fontSize: '0.75em', color: '#999', marginTop: '2px' }}>
                              Updated {new Date(e.conditions_updated_at).toLocaleDateString()}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span style={{ color: '#ccc', fontStyle: 'italic' }}>No reports yet</span>
                      )}
                    </td>
                  </tr>

                  {/* Expanded Detail */}
                  {selectedEntry?.id === e.id && (
                    <tr>
                      <td colSpan="9" style={{ backgroundColor: '#f8f9fa' }}>
                        <div className="p-3">
                          <div className="row">
                            <div className="col-md-3">
                              <h6>{e.insect_name} — {e.river_name}</h6>
                              <p className="mb-1"><strong>Region:</strong> {e.river_region}</p>
                              <p className="mb-1"><strong>Order:</strong> <OrderBadge order={e.insect_order} /></p>
                              <p className="mb-1"><strong>Hook Size:</strong> {formatHookSize(e)}</p>
                              <p className="mb-1"><strong>Window:</strong> {formatHatchWindow(e)}</p>
                              <p className="mb-1"><strong>Peak:</strong> {e.peak_months}</p>
                              <p className="mb-1"><strong>Status:</strong> <StatusBadge status={e.hatch_status} /></p>
                              {e.life_stages && e.life_stages.length > 0 && (
                                <p className="mb-1">
                                  <strong>Life Stages:</strong>{' '}
                                  {e.life_stages.map((s, i) => (
                                    <span key={i} style={{
                                      backgroundColor: '#e9ecef', padding: '1px 6px',
                                      borderRadius: '4px', fontSize: '0.8em', marginRight: '4px',
                                    }}>{s}</span>
                                  ))}
                                </p>
                              )}
                            </div>
                            <div className="col-md-4">
                              <h6 style={{ fontSize: '0.9em' }}>Recommended Flies</h6>
                              {e.recommended_flies && e.recommended_flies.length > 0 ? (
                                <ul style={{ fontSize: '0.85em', paddingLeft: '18px' }}>
                                  {e.recommended_flies.map((f, i) => <li key={i}>{f}</li>)}
                                </ul>
                              ) : <p className="text-muted">None listed</p>}
                            </div>
                            <div className="col-md-5">
                              <h6 style={{ fontSize: '0.9em' }}>Notes & Fishing Tips</h6>
                              <p style={{ fontSize: '0.85em', wordWrap: 'break-word', lineHeight: '1.5' }}>
                                {e.notes || 'No notes.'}
                              </p>
                              <div className="mt-3">
                                <button className="btn btn-sm btn-outline-danger"
                                  onClick={(ev) => { ev.stopPropagation(); handleDeleteEntry(e.id, e.insect_name); }}>
                                  Delete Entry
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default HatchTracker;
